// The start page's backdrop: a slow camera pan over the real Kabukicho in the 2x art. No server and no Pixi: the scene
// pieces come from the same worker painter the game uses (hd-client.ts) as ImageBitmaps and are blitted to a 2D canvas.
// Baked lights are already in the pieces; the live neon signs get additive glows and upright props are drawn foot-sorted.
import { buildMap } from '../../common/src/map';
import { HD_SCENES } from '../../common/src/data';
import { hdPieces, type HdPiece } from './hd-scene';
import { HdPainter, type HdPaintJob } from './hd-client';
import { PALETTE } from './theme';

/** canvas size = 640x360 world px at the art's 2x */
const W = 1280, H = 720, SCALE = 2;
/** camera loop (world px): the crossing, a block east along the 30th row, down Hachi's street and back (all open road) */
const LOOP: [number, number][] = [[672, 512], [1888, 512], [1888, 928], [672, 928]];
const SPEED = 16; // world px per second (about 3.5 minutes a lap)
const START: [number, number] = [1248, 512];

type Ready = { p: HdPiece; img: ImageBitmap | HTMLCanvasElement };
type Art = NonNullable<HdPaintJob['art']>;

/** Returns a stop function, or null when the city can't be shown. `onShown` fires with the first painted piece. */
export function startCityBackdrop(canvas: HTMLCanvasElement, animate: boolean, onShown: () => void): () => void {
  const map = buildMap(), painter = new HdPainter(map);
  const scenes = HD_SCENES.filter(s => s.id.startsWith('kabuki-') && s.id !== 'kabuki-north');
  const all = scenes.flatMap(hdPieces);
  // which pieces the camera will ever see
  const hw = W / SCALE / 2 + 40, hh = H / SCALE / 2 + 40;
  const bbox = animate ? { x0: 672, x1: 1888, y0: 512, y1: 928 } : { x0: START[0], x1: START[0], y0: START[1], y1: START[1] };
  let queue = all.filter(p => p.x + p.ww > bbox.x0 - hw && p.x < bbox.x1 + hw && p.y + p.wh > bbox.y0 - hh && p.y < bbox.y1 + hh);
  const ready: Ready[] = [], arts: Art[] = [], seenScenes = new Set<string>();
  const glows = new Map<string, HTMLCanvasElement>();
  const glow = (color: string) => {
    let c = glows.get(color);
    if (!c) {
      c = document.createElement('canvas'); c.width = c.height = 64; const g = c.getContext('2d')!;
      const r = g.createRadialGradient(32, 32, 0, 32, 32, 32); r.addColorStop(0, color + 'cc'); r.addColorStop(1, color + '00');
      g.fillStyle = r; g.fillRect(0, 0, 64, 64); glows.set(color, c);
    }
    return c;
  };

  const ctx = canvas.getContext('2d')!;
  let cam = { x: START[0], y: START[1] }, dist = 0, job: { p: HdPiece; job: HdPaintJob } | undefined, shown = false, stopped = false;
  const lens = LOOP.map((a, i) => { const b = LOOP[(i + 1) % LOOP.length]; return Math.hypot(b[0] - a[0], b[1] - a[1]); });
  const total = lens.reduce((a, b) => a + b, 0);
  const at = (d: number): [number, number] => {
    d = ((d % total) + total) % total;
    for (let i = 0; i < LOOP.length; i++) {
      if (d <= lens[i]) { const a = LOOP[i], b = LOOP[(i + 1) % LOOP.length], t = d / lens[i]; return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }
      d -= lens[i];
    }
    return LOOP[0];
  };
  // start halfway along the first leg, where the street starts
  dist = START[0] - LOOP[0][0];

  const drops = Array.from({ length: 90 }, (_, i) => ({ x: (i * 197) % W, y: (i * 89) % H, v: 380 + (i % 7) * 40 }));

  function pump() {
    if (!job) {
      if (!queue.length) return;
      queue.sort((a, b) => (a.x + a.ww / 2 - cam.x) ** 2 + (a.y + a.wh / 2 - cam.y) ** 2 - ((b.x + b.ww / 2 - cam.x) ** 2 + (b.y + b.wh / 2 - cam.y) ** 2));
      const p = queue.shift()!;
      job = { p, job: painter.start(p, !seenScenes.has(p.scene.id)) };
      seenScenes.add(p.scene.id);
    }
    if (job.job.run(6)) {
      ready.push({ p: job.p, img: job.job.canvas }); if (job.job.art) arts.push(job.job.art);
      job = undefined;
      if (!shown) { shown = true; canvas.width = W; canvas.height = H; onShown(); }
      return true;
    }
    return false;
  }

  function draw(tMs: number, dt: number) {
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = PALETTE.midnight; ctx.fillRect(0, 0, W, H);
    const ox = Math.round(W / 2 - cam.x * SCALE), oy = Math.round(H / 2 - cam.y * SCALE);
    for (const r of ready) ctx.drawImage(r.img, ox + Math.round(r.p.x * SCALE), oy + Math.round(r.p.y * SCALE));
    const props = arts.flatMap(a => a.props).filter(q => { const sx = ox + q.x * SCALE, sy = oy + q.y * SCALE; return sx > -200 && sx < W + 200 && sy > -100 && sy < H + 300; }).sort((a, b) => a.y - b.y);
    for (const q of props) ctx.drawImage(q.canvas, Math.round(ox + q.x * SCALE - q.ax), Math.round(oy + q.y * SCALE - q.ay));
    ctx.globalCompositeOperation = 'lighter';
    let i = 0;
    for (const a of arts) for (const l of a.lights) {
      i++;
      const gx = ox + (l.x + l.w / 2) * SCALE, gy = oy + (l.y + l.h / 2) * SCALE, gw = (l.w + 44) * SCALE, gh = (l.h + 34) * SCALE;
      if (gx < -gw || gx > W + gw || gy < -gh || gy > H + gh) continue;
      const flick = 0.82 + 0.18 * Math.sin(tMs / 1000 * 1.7 + i * 2.3);
      ctx.globalAlpha = Math.min(1, l.alpha * 1.1) * flick;
      ctx.drawImage(glow(l.color), gx - gw / 2, gy - gh / 2, gw, gh);
    }
    ctx.globalCompositeOperation = 'source-over';
    if (animate) {
      ctx.globalAlpha = 1; ctx.strokeStyle = PALETTE.offWhite + '26'; ctx.lineWidth = 1; ctx.beginPath();
      for (const d of drops) { d.y += d.v * dt; d.x -= d.v * 0.2 * dt; if (d.y > H) { d.y = -12; d.x = Math.random() * (W + 120); } ctx.moveTo(d.x, d.y); ctx.lineTo(d.x - 2, d.y + 10); }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  let raf = 0, last = 0;
  const loop = (ms: number) => {
    if (stopped) return;
    const idle = !job && !queue.length;
    if (!animate && idle && shown) { draw(ms, 0); return; } // one still frame, then stop
    raf = requestAnimationFrame(loop);
    if (document.hidden) { last = ms; return; }
    const painted = pump();
    if (!shown) return;
    if (animate && ms - last < 33 && !painted) return;
    const dt = Math.min(0.1, (ms - last) / 1000); last = ms;
    if (animate) {
      dist += SPEED * dt;
      const [tx, ty] = at(dist), k = Math.min(1, dt * 1.2);
      cam.x += (tx - cam.x) * k; cam.y += (ty - cam.y) * k;
    }
    draw(ms, dt);
  };
  raf = requestAnimationFrame(loop);
  return () => {
    stopped = true; cancelAnimationFrame(raf); painter.dispose();
    for (const r of ready) { if ('close' in r.img) r.img.close(); }
    for (const a of arts) for (const q of a.props) if ('close' in q.canvas) q.canvas.close();
    ready.length = 0; arts.length = 0; queue = []; job = undefined;
  };
}
